import { DashboardFilterValue } from '@/client/reports';
import { SecurityCultureSurvey } from '@/client/security-culture/types';
import {
  ChartEmptyState,
  ChartEmptyStateType,
} from '@/components/dashboards/admin';
import { SecurityCultureCard } from '@/components/security-culture';
import { useFilterableFeedback } from '@/hooks/query/security-culture.hooks';
import { useAppSelector } from '@/hooks/store';
import { useTable } from '@/hooks/table.hook';
import { selectCurrentAccount } from '@/store/features/account';
import { FlexContainer } from '@/ui/styled-ui';
import { ChartIcon } from '@/utils/helpers';
import { t } from 'i18next';
import React, { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { LoadingPage } from '../../LoadingPage';

const FeedbackRow = styled(FlexContainer)`
  justify-content: flex-start;
  gap: var(--small-padding);
  padding-bottom: var(--medium-padding);

  &:not(:last-child) {
    border-bottom: 1px solid var(--gray-dark);
  }

  svg {
    flex-shrink: 0;
    height: var(--default-padding);
    width: var(--default-padding);
  }
  span {
    font-size: var(--small-font-size);
    font-weight: 500;
    line-height: var(--small-line-height);
    overflow-x: auto;
  }
`;

type SCFeedbackProps = {
  filters?: DashboardFilterValue;
  selectedSurveys?: SecurityCultureSurvey[];
};

export const SCFeedback: React.FC<SCFeedbackProps> = ({
  filters,
  selectedSurveys,
}) => {
  const currentAccount = useAppSelector(selectCurrentAccount);
  const { surveyId } = useParams();
  const selectedSurveyIds = useMemo(
    () =>
      selectedSurveys
        ?.map((survey) => survey.id)
        .filter((id): id is string => Boolean(id)),
    [selectedSurveys],
  );
  const routeSurveyIds = useMemo(
    () => surveyId?.split(',').filter(Boolean),
    [surveyId],
  );
  const surveyIdsArray = selectedSurveyIds?.length
    ? selectedSurveyIds
    : routeSurveyIds;

  const { skip, take, onGridPageChange, onGridRowChange } = useTable();

  const { filteredFeedback: feedback, isLoading } = useFilterableFeedback({
    accountId: currentAccount?.id,
    surveyId: surveyIdsArray,
    branches: filters?.branches?.map((branch) => branch?.id),
    groups: filters?.groups?.map((group) => group?.id),
    departments: filters?.departments?.map((department) => department?.name),
    managers: filters?.managers?.map((manager) => manager?.name),
    skip,
    take,
  });

  if (isLoading || !feedback) {
    return <LoadingPage message={t('generic.loading')} />;
  }

  return (
    <SecurityCultureCard
      id="sc-feedback-page"
      title={t('security.culture.feedback')}
      content={
        <>
          {feedback.count ? (
            <>
              {feedback.result.map((x) => (
                <FeedbackRow key={x.id}>
                  <ChartIcon />
                  <span>{x.description}</span>
                </FeedbackRow>
              ))}
            </>
          ) : (
            <ChartEmptyState
              type={ChartEmptyStateType.SC}
              noBorder
              minHeight={200}
              title={t('security.culture.feedback.empty.title')}
              description={t('security.culture.feedback.empty.description')}
            />
          )}
        </>
      }
      take={take}
      skip={skip}
      count={feedback.count}
      onPageChange={onGridPageChange}
      onRowsChange={onGridRowChange}
    />
  );
};
